import React from 'react';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Users, Vote } from 'lucide-react';

const ConsoleVotingPanel = ({ selectedItem, setSelectedItem, updateAgendaItem, isStockholderMeeting, attendance, users }) => {
  if (!selectedItem) {
    return (
      <div className="text-center py-8 text-[#5A6570]">
        <Vote className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
        <p>Select an agenda item first to record votes</p>
      </div>
    );
  }

  const countField = isStockholderMeeting ? 'shares' : 'votes';
  const presentUserIds = attendance.filter(a => a.status === 'present_onsite' || a.status === 'present_virtual').map(a => a.user_id);
  const presentUsers = users.filter(u => presentUserIds.includes(u.id));
  const records = selectedItem.voter_records || [];

  const handleVote = (userId, userName, currentVote, vote) => {
    const newVote = currentVote === vote ? '' : vote;
    const updated = records.filter(r => r.user_id !== userId);
    if (newVote) updated.push({ user_id: userId, user_name: userName, vote: newVote });
    const votesFor = updated.filter(r => r.vote === 'for').length;
    const votesAgainst = updated.filter(r => r.vote === 'against').length;
    const votesAbstain = updated.filter(r => r.vote === 'abstain').length;
    const syncFields = isStockholderMeeting
      ? { voter_records: updated, shares_for: votesFor, shares_against: votesAgainst, shares_abstain: votesAbstain }
      : { voter_records: updated, votes_for: votesFor, votes_against: votesAgainst, votes_abstain: votesAbstain };
    setSelectedItem(prev => ({ ...prev, ...syncFields }));
    updateAgendaItem(selectedItem.id, syncFields);
  };

  const forCount = records.filter(r => r.vote === 'for').length;
  const againstCount = records.filter(r => r.vote === 'against').length;
  const abstainCount = records.filter(r => r.vote === 'abstain').length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-[#1A1D21]">{selectedItem.title}</h3>
        {selectedItem.for_voting_subject ? (
          <div className="mt-2 p-3 rounded-lg border border-[#F58A26]/30 bg-[#FFF7ED]" data-testid="voting-subject-display">
            <p className="text-xs font-medium text-[#F58A26] mb-1">Subject for Voting</p>
            <p className="text-sm text-[#1A1D21]">{selectedItem.for_voting_subject}</p>
          </div>
        ) : (
          <p className="text-sm text-[#5A6570] mt-1">Record the voting results as decided by the Body / Presiding Officer.</p>
        )}
      </div>

      {/* Vote Totals */}
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-lg border border-green-200 bg-green-50">
            <Label className="text-xs text-green-800">{isStockholderMeeting ? 'Shares' : 'Votes'} For</Label>
            <p className="mt-2 text-2xl font-bold text-center text-green-800">{selectedItem[`${countField}_for`] || 0}</p>
          </div>
          <div className="p-4 rounded-lg border border-red-200 bg-red-50">
            <Label className="text-xs text-red-800">{isStockholderMeeting ? 'Shares' : 'Votes'} Against</Label>
            <p className="mt-2 text-2xl font-bold text-center text-red-800">{selectedItem[`${countField}_against`] || 0}</p>
          </div>
          <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
            <Label className="text-xs text-gray-800">{isStockholderMeeting ? 'Shares' : ''} Abstain</Label>
            <p className="mt-2 text-2xl font-bold text-center text-gray-800">{selectedItem[`${countField}_abstain`] || 0}</p>
          </div>
        </div>
        <div className="p-4 bg-[#F8F9FA] rounded-lg flex items-center justify-between">
          <span className="font-medium text-[#1A1D21]">Result{isStockholderMeeting ? ' (by shares)' : ''}</span>
          <span className={`text-lg font-bold ${
            (selectedItem[`${countField}_for`] || 0) > (selectedItem[`${countField}_against`] || 0) ? 'text-green-600' :
            (selectedItem[`${countField}_for`] || 0) === (selectedItem[`${countField}_against`] || 0) ? 'text-amber-600' : 'text-red-600'
          }`}>
            {(selectedItem[`${countField}_for`] || 0) > (selectedItem[`${countField}_against`] || 0) ? 'APPROVED' :
             (selectedItem[`${countField}_for`] || 0) === (selectedItem[`${countField}_against`] || 0) ? 'TIE' : 'REJECTED'}
          </span>
        </div>
      </div>

      {/* Voter Checklist */}
      <div>
        <Label className="text-sm font-semibold text-[#1A1D21] mb-2 block">Individual Vote Records</Label>
        <p className="text-xs text-[#5A6570] mb-3">Mark how each member voted on this item.</p>
        <div className="border border-[#E2E6EA] rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_auto] items-center gap-2 px-4 py-2 bg-[#F0F2F4] border-b border-[#E2E6EA]">
            <span className="text-xs font-semibold text-[#5A6570]">Member</span>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-green-700 w-16 text-center">For</span>
              <span className="text-xs font-semibold text-red-700 w-16 text-center">Against</span>
              <span className="text-xs font-semibold text-gray-600 w-16 text-center">Abstain</span>
            </div>
          </div>
          <div className="divide-y divide-[#E2E6EA] max-h-[280px] overflow-y-auto">
            {presentUsers.length === 0 ? (
              <div className="text-center py-6 text-[#A8B1B9]"><Users className="h-6 w-6 mx-auto mb-2" /><p className="text-xs">No attendees marked as present</p></div>
            ) : presentUsers.map(u => {
              const record = records.find(r => r.user_id === u.id);
              const currentVote = record?.vote || '';
              return (
                <div key={u.id} className="grid grid-cols-[1fr_auto] items-center gap-2 px-4 py-2.5 hover:bg-[#F8F9FA] transition-colors" data-testid={`voter-record-${u.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1A1D21] truncate">{u.full_name}</p>
                    <p className="text-[10px] text-[#5A6570] capitalize">{u.role?.replace('_', ' ')}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {['for', 'against', 'abstain'].map(vote => (
                      <button key={vote} onClick={() => handleVote(u.id, u.full_name, currentVote, vote)}
                        className={`w-16 h-7 rounded text-xs font-medium transition-all border ${
                          currentVote === vote
                            ? vote === 'for' ? 'bg-green-600 text-white border-green-600'
                              : vote === 'against' ? 'bg-red-600 text-white border-red-600'
                              : 'bg-gray-600 text-white border-gray-600'
                            : 'bg-white text-[#5A6570] border-[#E2E6EA] hover:border-[#00A3A3]'
                        }`}
                        data-testid={`voter-${u.id}-${vote}`}
                      >
                        {vote === 'for' ? 'For' : vote === 'against' ? 'Against' : 'Abstain'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {records.length > 0 && (
            <div className="px-4 py-2.5 bg-[#F8F9FA] border-t border-[#E2E6EA] flex items-center justify-between" data-testid="voter-tally">
              <span className="text-xs font-medium text-[#5A6570]">Checklist Tally:</span>
              <div className="flex items-center gap-4 text-xs font-semibold">
                <span className="text-green-700">{forCount} For</span>
                <span className="text-red-700">{againstCount} Against</span>
                <span className="text-gray-600">{abstainCount} Abstain</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Secretary Notes */}
      <div>
        <Label className="text-sm font-medium text-[#1A1D21]">Secretary Notes</Label>
        <Textarea value={selectedItem.notes || ''} onChange={e => setSelectedItem({ ...selectedItem, notes: e.target.value })}
          onBlur={() => updateAgendaItem(selectedItem.id, { notes: selectedItem.notes })}
          placeholder="Secretary's notes on the voting..." className="mt-1 border-[#E2E6EA] min-h-[60px]" data-testid="voting-secretary-notes" />
      </div>
    </div>
  );
};

export default ConsoleVotingPanel;
